import { useState, useEffect } from "react";
import {
  Button,
  CardHeader,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Box,
  DataGrid,
  IconButton,
  Tooltip,
  DatePicker,
  MaskedInput,
  Autocomplete,
} from "../../../../components";
import {
  notification,
  ClienteService,
} from "../../../../services";
import { InputErros, value } from "../../../../helpers";
import { formatCelular } from "../../../../utils";
import { clienteValidator } from "../middlewares";

function DependentesDialog({ open, setOpen, cliente, setCliente, modelosGrade }) {
  const clienteService = new ClienteService();
  const [dependentesClientes, setDependentesClientes] = useState([]);
  const [formDependente, setFormDependente] = useState({
    id: null,
    nome: "",
    dataNascimentoDependente: null,
    celularDependente: "",
    emailDependente: "",
    observacaoDependente: "",
    gradeFavorita: []
  });
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (!open) {
      setFormDependente({
        id: null,
        nome: "",
        dataNascimentoDependente: null,
        celularDependente: "",
        emailDependente: "",
        observacaoDependente: "",
        gradeFavorita: []
      });
    } else {
      if (cliente?.dependentes?.length) {
        setDependentesClientes(cliente.dependentes);
      }
    }
  }, [open, cliente]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormDependente((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDependente((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (fieldName, date) => {
    setFormDependente((prev) => ({ ...prev, [fieldName]: date }));
  };

  const handleAddUpdateDependente = () => {
    clienteValidator
      .validate(
        {
          ...cliente,
          dependentes: [formDependente, ...(cliente?.dependentes || [])],
        },
        { abortEarly: false }
      )
      .then(async () => {
        setInputErro([]);
        if (formDependente.id) {
          setDependentesClientes((prevState) =>
            prevState.map((app) =>
              app.id === formDependente.id ? { ...formDependente } : app
            )
          );
        } else {
          const newId =
            dependentesClientes.length > 0
              ? Math.max(...dependentesClientes.map((app) => app.id)) + 1
              : 1;
          setDependentesClientes((prevState) => [
            ...prevState,
            { ...formDependente, id: newId },
          ]);
        }

        setFormDependente({
          id: null,
          nome: "",
          dataNascimentoDependente: null,
          celularDependente: "",
          emailDependente: "",
          observacaoDependente: "",
          gradeFavorita: []
        });
      })
      .catch((err) => {
        inputErros.set(err);
        return;
      });
  };

  const handleEditDependente = (id) => {
    const dependenteEditado = dependentesClientes.find((app) => app.id === id);
    if (dependenteEditado) {
      setFormDependente(dependenteEditado);
    }
  };

  const handleDeleteDependente = (id) => {
    setDependentesClientes((prevState) =>
      prevState.filter((app) => app.id !== id)
    );

    if (formDependente.id === id) {
      setFormDependente({
        id: null,
        nome: "",
        dataNascimentoDependente: null,
        celularDependente: "",
        emailDependente: "",
        observacaoDependente: "",
        gradeFavorita: []
      });
    }
  };

  const handleSave = async () => {
    const dependentes = dependentesClientes.map((app) => ({
      nome: app.nome,
      dataNascimentoDependente: app.dataNascimentoDependente,
      celularDependente: app.celularDependente,
      emailDependente: app.emailDependente,
      observacaoDependente: app.observacaoDependente,
      gradeFavorita: app.gradeFavorita,
    }));
    setCliente((prevState) => ({ ...prevState, dependentes }));
    if (cliente?.id) {
      const response = await clienteService.atualizarDependentes(
        cliente.id,
        dependentes
      );
      if (!response.isAxiosError) {
        notification.alteracaoSucesso();
      }
    }
    handleClose();
  };

  const formatarGradeFavorita = (gradeFavorita) => {
    return gradeFavorita.map(({valor}) => valor).join(' | ');
  };

  const getMultipleAutocompleteValues = (valores, lista) => {
    if (valores) {
      return valores.map((valor) => value.autoComplete(lista, valor.id));
    }
    return [];
  };

  const colunas = [
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.value?.toUpperCase() || "",
    },
    {
      field: "celularDependente",
      headerName: "Celular",
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => formatCelular(params.value),
    },
    {
      field: "emailDependente",
      headerName: "E-mail",
      sortable: false,
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "gradeFavorita",
      headerName: "Grade Favorita",
      sortable: false,
      flex: 2,
      minWidth: 300,
      valueGetter: (params) => formatarGradeFavorita(params.value),
    },
    {
      field: "actions",
      headerName: "Ações",
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            color="info"
            size="small"
            onClick={() => handleEditDependente(params.row.id)}
          >
            <Tooltip title="Editar" arrow>
              <i className="ph-fill ph-pencil" style={{ fontSize: 20 }}></i>
            </Tooltip>
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDeleteDependente(params.row.id)}
          >
            <Tooltip title="Excluir" arrow>
              <i className="ph-fill ph-trash" style={{ fontSize: 20 }}></i>
            </Tooltip>
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Dialog maxWidth={"xl"} fullWidth open={open} onClose={handleClose}>
      <DialogContent>
        <CardHeader
          title="Dependentes de Cliente"
          action={
            <Button onClick={handleClose} variant="contained" color="padrao">
              Fechar
            </Button>
          }
        />
        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Nome"
              variant="outlined"
              fullWidth
              name="nome"
              required
              value={formDependente.nome}
              onChange={handleFormChange}
              margin="normal"
              error={inputErros.get("nome")}
            />
            <DatePicker
              id={"dataNascimentoDependente"}
              label={"Data de Nascimento"}
              name={"dataNascimentoDependente"}
              minDate={new Date(1900, 1, 1)}
              maxDate={new Date()}
              format="dd/MM/yyyy"
              onChange={(date) => {
                handleDateChange("dataNascimentoDependente", date);
              }}
              required
              margin="normal"
              error={inputErros.get("dataNascimentoDependente")}
              value={formDependente.dataNascimentoDependente || null}
            />
            <MaskedInput
              id="celularDependente"
              label="Celular"
              name="celularDependente"
              margin="normal"
              required
              value={value.text(formDependente.celularDependente)}
              mask="(99) 9 9999-9999"
              error={inputErros.get("celularDependente")}
              onChange={(e) => handleInputChange(e)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <IconButton disabled={true}>
                    <i
                      className="ph-fill ph-device-mobile"
                      style={{
                        color: "#494C62",
                        fontSize: 20,
                      }}
                    ></i>
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="E-mail"
              name="emailDependente"
              id="emailDependente"
              type="email"
              variant="outlined"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleInputChange(e)}
              value={formDependente.emailDependente || ""}
              fullWidth
            />
            <TextField
              id="observacaoDependente"
              name="observacaoDependente"
              label="Observação"
              margin="normal"
              fullWidth
              multiline
              rows={2}
              value={formDependente.observacaoDependente}
              onChange={handleFormChange}
              variant="outlined"
              size="small"
            />
            <Autocomplete
              multiple
              id="gradeFavorita"
              name="gradeFavorita"
              options={modelosGrade}
              noOptionsText="Sem opções"
              autoHighlight
              getOptionLabel={(option) => (option ? option.valor : "")}
              onChange={(_, newValue) => {
                setFormDependente((prevState) => ({
                  ...prevState,
                  gradeFavorita: newValue,
                }));
              }}
              value={getMultipleAutocompleteValues(
                formDependente?.gradeFavorita,
                modelosGrade
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modelos de Grade Favoritos"
                  margin="normal"
                  variant={"outlined"}
                />
              )}
            />
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleAddUpdateDependente}
            >
              {formDependente.id
                ? "Atualizar Dependente"
                : "Adicionar Dependente"}
            </Button>
            {formDependente.id && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => {
                  setFormDependente({
                    id: null,
                    nome: "",
                    dataNascimentoDependente: null,
                    celularDependente: "",
                    emailDependente: "",
                    observacaoDependente: "",
                    gradeFavorita: []
                  });
                }}
              >
                Limpar Formulário
              </Button>
            )}
          </Grid>

          <Grid container xs={1} className="d-flex justify-content-center">
            <hr className="vr" />
          </Grid>

          <Grid item xs={12} md={8} sx={{ mt: 2 }}>
            <DataGrid
              rows={dependentesClientes}
              columns={colunas}
              disableAcoes
              disableRowSelectionOnClick
              hideFooterPagination
              getRowId={(row) => row.nome + row.celular}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", m: 2, gap: 1 }}>
          <Tooltip title="Salvar todos os registros" placement="left">
            <Button onClick={handleSave} variant="contained" color="primary">
              Salvar
            </Button>
          </Tooltip>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default DependentesDialog;
